// Pure helpers behind the Signups dashboard's "Edit access" modal
// (SuperAdmin.js). public.users.schema is a comma list whose FIRST entry is
// the user's native/active schema (the one loadSupabaseSchema opens at
// login); the rest are extra schemas they can switch to. Kept here, away
// from the 4k-line page, so the list/role rules are unit-testable.
import { getTenantSchemaForAccountType } from '../config/roles';

export function parseSchemaList(csv) {
  return String(csv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

// Make `native` the first entry without losing any schema: an existing extra
// swaps places with the old native; a schema not in the list takes the first
// slot and the old native becomes the first extra. Blank → list unchanged.
export function setNativeSchema(list, native) {
  const name = String(native || '').trim();
  const out = [...list];
  if (!name || out[0] === name) return out;
  const at = out.indexOf(name);
  if (at > 0) {
    [out[0], out[at]] = [out[at], out[0]];
    return out;
  }
  return [name, ...out];
}

// The editors keep the list as it was before the native input was touched
// (`base`) and derive the saved list with setNativeSchema(base, nativeInput),
// so each keystroke in the native input recomputes from the same base
// instead of piling "S", "ST", "STU"… into the list.
export function addDraftSchema(base, nativeInput, name) {
  const trimmed = String(name || '').trim();
  if (!trimmed || setNativeSchema(base, nativeInput).includes(trimmed)) return [...base];
  return [...base, trimmed];
}

export function removeDraftSchema(base, name) {
  return base.filter((s) => s !== name);
}

export function addExtraSchema(list, name) {
  const trimmed = String(name || '').trim();
  if (!trimmed || list.includes(trimmed)) return [...list];
  return [...list, trimmed];
}

// Extras only — the native entry is changed through setNativeSchema.
export function removeExtraSchema(list, name) {
  return list.filter((s, i) => i === 0 || s !== name);
}

// Mirror of the backend's _role_with_pending_prefix: a pending signup
// ("user,student" / "unauthenticated,student") keeps its prefix so the
// Accept button survives and approves as the NEW role.
export function roleWithPendingPrefix(currentRole, newRole) {
  const lowered = String(currentRole || '').trim().toLowerCase();
  if (lowered.startsWith('user,')) return `user,${newRole}`;
  if (lowered.startsWith('unauthenticated,')) return `unauthenticated,${newRole}`;
  return newRole;
}

// Tenant roles (student/socialworker/other/medstudent) normally live in their
// own schema. Returns that expected schema when the chosen native schema is a
// different one, so the modal can hint; null when there is nothing to flag.
export function tenantSchemaMismatch(role, nativeSchema) {
  const expected = getTenantSchemaForAccountType(role);
  if (!expected) return null;
  if (String(nativeSchema || '').trim().toUpperCase() === expected) return null;
  return expected;
}
